import React, { useEffect, useState } from 'react';
import {
  Table,
  Tag,
  Select,
  Button,
  Card,
  Statistic,
  Row,
  Col,
  Modal,
  Input,
  message,
  Space,
} from 'antd';
import request from '../utils/request';
import { fmtTime, stars } from '../utils/format';

const typeMap: Record<string, { color: string; text: string }> = {
  COMPLAINT: { color: 'red', text: '投诉' },
  SUGGESTION: { color: 'blue', text: '建议' },
  REVIEW: { color: 'gold', text: '评价' },
};

const statusMap: Record<number, { color: string; text: string }> = {
  1: { color: 'orange', text: '未处理' },
  2: { color: 'blue', text: '处理中' },
  3: { color: 'green', text: '已处理' },
};

const Feedbacks: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [detail, setDetail] = useState<any>(null);
  const [replying, setReplying] = useState<any>(null);
  const [replyText, setReplyText] = useState('');
  const [handleStatus, setHandleStatus] = useState<number>(3);
  const [statusCount, setStatusCount] = useState<Record<number, number>>({});
  // 回复在途锁：Modal 的 onOk 不感知 promise，防连点重复提交
  const [submittingReply, setSubmittingReply] = useState(false);

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const params: any = { page: p, pageSize: 10 };
      if (type) params.type = type;
      if (status !== undefined) params.status = status;
      const res: any = await request.get('/feedback', { params });
      setList(res.list);
      setTotal(res.total);
      setStatusCount(res.statusCount || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page, type, status]);

  const openReply = (record: any) => {
    setReplying(record);
    setReplyText(record.reply || '');
    setHandleStatus(record.status === 1 ? 3 : record.status);
  };

  const submitReply = async () => {
    if (handleStatus === 3 && !replyText.trim()) {
      message.error('解决前请填写回复内容');
      return;
    }
    if (submittingReply) return;
    setSubmittingReply(true);
    try {
      await request.put(`/feedback/${replying.id}`, {
        status: handleStatus,
        reply: replyText.trim() || undefined,
      });
      message.success(handleStatus === 3 ? '已解决并通知用户' : '已更新处理状态');
      setReplying(null);
      fetchList();
    } finally {
      setSubmittingReply(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '类型', dataIndex: 'type', width: 90,
      render: (v: string) => <Tag color={typeMap[v]?.color}>{typeMap[v]?.text}</Tag>,
    },
    { title: '用户', dataIndex: ['user', 'realName'], render: (v: string, r: any) => v || r.user?.nickname || `用户${r.userId}` },
    { title: '手机号', dataIndex: ['user', 'phone'] },
    {
      title: '评分', dataIndex: 'rating', width: 90,
      render: (v: number) => stars(v),
    },
    {
      title: '内容', dataIndex: 'content', ellipsis: true,
      render: (v: string) => <span title={v}>{v}</span>,
    },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v: number) => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text || v}</Tag>,
    },
    { title: '提交时间', dataIndex: 'createdAt', width: 160, render: (v: string) => fmtTime(v) },
    {
      title: '操作', width: 180, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => setDetail(record)}>详情</Button>
          <Button size="small" type="primary" onClick={() => openReply(record)}>
            {record.status === 3 ? '修改回复' : '处理'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>反馈管理</h2>
        <Space>
          <Select allowClear placeholder="筛选类型" style={{ width: 120 }} value={type}
            onChange={(v) => { setType(v); setPage(1); }}
            options={[
              { label: '投诉', value: 'COMPLAINT' },
              { label: '建议', value: 'SUGGESTION' },
              { label: '评价', value: 'REVIEW' },
            ]} />
          <Select allowClear placeholder="筛选状态" style={{ width: 120 }} value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            options={[
              { label: '未处理', value: '1' },
              { label: '处理中', value: '2' },
              { label: '已处理', value: '3' },
            ]} />
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card size="small"><Statistic title="总反馈数" value={(statusCount[1] || 0) + (statusCount[2] || 0) + (statusCount[3] || 0)} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="未处理" value={statusCount[1] || 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="处理中" value={statusCount[2] || 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="已处理" value={statusCount[3] || 0} /></Card></Col>
      </Row>

      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1000 }}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />

      <Modal title="反馈详情" open={!!detail} footer={null} onCancel={() => setDetail(null)} width={640}>
        {detail && (
          <div>
            <p><Tag color={typeMap[detail.type]?.color}>{typeMap[detail.type]?.text}</Tag></p>
            <p><strong>用户：</strong>{detail.user?.realName || detail.user?.nickname || `用户${detail.userId}`}</p>
            <p><strong>手机号：</strong>{detail.user?.phone || '-'}</p>
            {detail.rating > 0 && <p><strong>评分：</strong>{stars(detail.rating)}</p>}
            <p><strong>内容：</strong>{detail.content}</p>
            <p><strong>状态：</strong><Tag color={statusMap[detail.status]?.color}>{statusMap[detail.status]?.text}</Tag></p>
            {detail.reply && (
              <div style={{ background: '#f6ffed', padding: 12, borderRadius: 8, marginTop: 12 }}>
                <p style={{ margin: 0 }}><strong>回复：</strong>{detail.reply}</p>
                {detail.repliedAt && (
                  <p style={{ margin: '8px 0 0', color: '#999' }}>处理于 {fmtTime(detail.repliedAt, true)}</p>
                )}
              </div>
            )}
            <p style={{ color: '#999', marginTop: 16 }}>提交时间：{fmtTime(detail.createdAt, true)}</p>
          </div>
        )}
      </Modal>

      <Modal
        title="处理反馈"
        open={!!replying}
        onOk={submitReply}
        onCancel={() => !submittingReply && setReplying(null)}
        okButtonProps={{ loading: submittingReply }}
        okText="提交"
        cancelText="取消"
      >
        <p>用户反馈：{replying?.content}</p>
        <Space style={{ marginBottom: 12 }}>
          <span>处理状态：</span>
          <Select
            style={{ width: 140 }}
            value={handleStatus}
            onChange={setHandleStatus}
            options={[
              { label: '处理中', value: 2 },
              { label: '已处理（解决）', value: 3 },
            ]}
          />
        </Space>
        <Input.TextArea
          rows={4}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder={handleStatus === 3 ? '回复内容必填，将以站内消息通知用户' : '可先不回复，标记处理中'}
        />
      </Modal>
    </div>
  );
};

export default Feedbacks;
